import {
    Async,
    Deferred,
    eq,
    GoogleMap,
    Instance,
    Knot,
    LatLng,
    noop,
    Objekt,
    pluck,
    Promize,
    Table,
} from '@siposdani87/sui-js';
import { app } from '../app';
import { resources } from '../resources';
import { AreaService } from '../services/areaService';
import { BigGameTrophyService } from '../services/bigGameTrophyService';
import { LanguageService } from '../services/languageService';
import { MapService } from '../services/mapService';
import { PlanPreyService } from '../services/planPreyService';
import { SmallGameTrophyService } from '../services/smallGameTrophyService';
import { TrophyService } from '../services/trophyService';
import { UserService } from '../services/userService';
import { UtilService } from '../services/utilService';
import { DashboardController } from './dashboardController';

export class PlanPreysShowController extends DashboardController {
    planId: string;
    planPreyId: string;
    planPrey: Objekt;
    btnTrophiesNewKnot: Knot<HTMLElement>;
    trophiesTable: Table;
    hasAccessTrophyEdit: boolean;
    area: Objekt;
    trophies: Objekt[];
    map: GoogleMap;

    constructor(
        instances: Instance,
        private areaService: AreaService,
        private planPreyService: PlanPreyService,
        private trophyService: TrophyService,
        private smallGameTrophyService: SmallGameTrophyService,
        private bigGameTrophyService: BigGameTrophyService,
        private mapService: MapService,
        private languageService: LanguageService,
        private utilService: UtilService,
        private userService: UserService,
    ) {
        super(instances);
    }

    protected override _initLayout(): void {
        this.planId = this.state.getParam('planId');
        this.planPreyId = this.state.getParam('planPreyId');
        this.planPrey = new Objekt();

        this._initToolbarButtons();
        this._initTabPanel('trophy-heatmap');
        this._changeToolbarButtons();

        this._loadPrey().then(() => {
            this._initAccess();
            this._drawContent();
        });
    }

    private _initToolbarButtons(): void {
        this.helper.iconButton(
            '.btn-plans-show',
            this.dom,
            () => {
                this.state.goBack('plans.show', {
                    planId: this.planPrey.get('plan.id'),
                });
            },
            '',
            true,
            [],
        );

        this.btnTrophiesNewKnot = this.helper.iconButton(
            '.btn-trophies-new',
            this.dom,
            () => {
                if (eq(this.planPrey.get('classification'), 'small')) {
                    this.smallGameTrophyService
                        .newDialogByPlanPrey(this.planPreyId)
                        .then(() => {
                            this._drawContent();
                        });
                } else {
                    this.bigGameTrophyService
                        .newDialogByPlanPrey(this.planPreyId)
                        .then(() => {
                            this._drawContent();
                        });
                }
            },
        );
    }

    private _initTrophyTable(): void {
        if (eq(this.activeTab, 'trophy-table')) {
            if (!this.trophiesTable) {
                this.trophiesTable = new Table(
                    this.dom,
                    '.trophies-table',
                    this.trophyService.getTableOptions(),
                );

                this.trophiesTable.setActions([
                    {
                        style: (trophy) => {
                            return [
                                'info',
                                trophy.get('description'),
                                false,
                                !trophy.get('description'),
                            ];
                        },
                        click: noop(),
                    },
                    {
                        style: () => {
                            return [
                                'edit',
                                this.languageService.translate('buttons.edit'),
                                false,
                                !this.hasAccessTrophyEdit,
                            ];
                        },
                        click: (trophy) => {
                            this.trophyService.editDialog(trophy).then(() => {
                                this.trophiesTable.refresh();
                            });
                        },
                    },
                ]);

                this.trophiesTable.eventAction = (params) => {
                    params.set('fields', this.trophyService.getTableFields());
                    this.trophyService
                        .getAllByPlanAndPlanPrey(
                            this.planId,
                            this.planPreyId,
                            params,
                        )
                        .then((response) => {
                            const count = response.get<number>('count');
                            this.trophiesTable.setCount(count);
                            const trophies = response.get<Objekt[]>('trophies');
                            this.trophiesTable.setData(trophies);
                        });
                };

                this.trophiesTable.render();
            } else {
                this.trophiesTable.refresh();
            }
        }
    }

    private _loadArea(): Promize {
        const deferred = new Deferred();
        this.areaService
            .getAllByOrganization(this.planPrey.get('plan.organization_id'))
            .then(
                (response) => {
                    this.area = response.get('areas', [])[0] || new Objekt();
                    deferred.resolve();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _loadTrophies(): Promize {
        const deferred = new Deferred();
        this.trophyService
            .getAllByPlanAndPlanPrey(this.planId, this.planPreyId, {
                fields: 'location',
            })
            .then(
                (response) => {
                    this.trophies = response.get('trophies');
                    deferred.resolve();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _loadPrey(): Promize {
        const deferred = new Deferred();
        this.planPreyService
            .getByPlan(this.planId, this.planPreyId, {
                fields: 'id,classification,plan.id,plan.organization_id',
            })
            .then(
                (response) => {
                    this.planPrey = response.get('plan_prey');
                    deferred.resolve();
                },
                () => {
                    deferred.reject();
                },
            );
        return deferred.promise();
    }

    private _initAccess(): void {
        this.hasAccessTrophyEdit = this.userService.hasAccessByOrganization(
            this.planPrey.get('plan.organization_id'),
            'trophies_write',
        );
    }

    protected override _drawContent(): Promize | boolean {
        this._changeToolbarButtons();

        this._initTrophyTable();
        this._initHeatmap();
        return true;
    }

    private _changeToolbarButtons(): void {
        this.utilService.showButton(
            this.btnTrophiesNewKnot,
            this.activeTab === 'trophy-table',
        );
    }

    private _initHeatmap(): void {
        if (eq(this.activeTab, 'trophy-heatmap')) {
            if (!this.map) {
                this.map = this.mapService.createGoogleMap(this.dom, '.map', {
                    mapTypeId: 'custom',
                    mapTypeControlOptions: {
                        mapTypeIds: ['custom', google.maps.MapTypeId.SATELLITE],
                    },
                    mapTypeControl: true,
                    scrollwheel: true,
                });

                this.map.setPolygons(this.mapService.getAreaPolygonOptions());

                this.map.setHeatmap(this.mapService.getHeatMapOptions());
            }

            const async = new Async();
            async
                .parallel([
                    this._loadArea.bind(this),
                    this._loadTrophies.bind(this),
                ])
                .then(() => {
                    this._setMap();
                });
        }
    }

    private _setMap(): void {
        this.map.removeAllPolygon();
        this.map.removeHeatmap();

        const areaId = this.area.get<string>('id');
        const areaPoints = this.area.get<LatLng[]>('locations', []);
        this.map.createPolygon(areaId, '', areaPoints, this.area);
        this.map.fitPolygonToMap(areaId);

        const points = pluck<{ latitude: number; longitude: number }>(
            this.trophies,
            'location',
        );
        this.map.createHeatmap(this.mapService.getWeightedLocations(points));
    }

    exit(): void {
        super.exit();

        this.mapService.removeGoogleMap(this.map);
    }
}

export const planPreysShowController = app.controller(
    resources.planPreysShowController,
    [
        resources.instances,
        resources.areaService,
        resources.planPreyService,
        resources.trophyService,
        resources.smallGameTrophyService,
        resources.bigGameTrophyService,
        resources.mapService,
        resources.languageService,
        resources.utilService,
        resources.userService,
    ],
    PlanPreysShowController,
);
